/* eslint-disable complexity */
import { useEffect, useContext } from "react";

import { css } from "@compiled/react";
import { useInView } from "react-intersection-observer";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import UFOSegment from "@atlassian/wac-ufo-segment";

import { Adg4Button } from "@/components/adg4/button/src";
import Link from "@/components/wpl/link/src";

import { WithRequired } from "../../../../utils/typescript-utils";

import { TeamType, ScrollContext } from "./scroll-pinned-sections";

export const HomepageSectionScrollItem = (
    props: WithRequired<TeamType, "index">
) => {
    const intl = useIntl();
    const { ref: scrollRef, inView: sectionIsVisible } = useInView({
        threshold: 0.1,
        rootMargin: "-40% 0px -40% 0px"
    });
    const learnMoreText = intl.formatMessage({
        id: "HomePage.ScrollPinnedSection.LearnMoreText",
        defaultMessage: "Learn more"
    });
    const {
        setActiveAccentImg,
        setActiveBrand,
        setActiveColor,
        setActiveProductCTAText,
        setActiveProductCTAUrl,
        setActiveCustomerCTAUrl,
        setActiveShape,
        setActiveFeatureVideo,
        setActivePosterImage,
        setActiveIndex
    } = useContext(ScrollContext);
    useEffect(() => {
        if (sectionIsVisible) {
            setActiveAccentImg(props.accentImg);
            setActiveBrand(props.brand);
            setActiveColor(props.bgColor);
            setActiveProductCTAText(props.productCTA.text);
            setActiveProductCTAUrl(props.productCTA.url);
            setActiveCustomerCTAUrl(props.customerCTAUrl);
            setActiveIndex(props.index || 0);
            setActiveShape(props.bgShape);
            setActiveFeatureVideo(props.featureVideo);
            setActivePosterImage(props.posterImage);
        }
    });

    return (
        <UFOSegment name="homepage-section-scroll-item">
            <div className="team-type" css={teamTypeStyles}>
                <div
                    className={`team-card ${
                        sectionIsVisible ? "active" : "inactive"
                    }`}
                    css={teamCardStyles}
                    ref={scrollRef}
                >
                    <div css={[logoStyles]}>
                        {props.logo}
                        <h4 className="h6" css={h4Styles}>
                            {props.productName}
                        </h4>
                    </div>
                    <h3 css={h3Styles}>{props.header}</h3>
                    <p className="body-lg" css={descriptionStyles}>
                        {props.description}
                    </p>
                    <div css={quoteStyles}>
                        <p className="body-sm" css={quotePStyles}>
                            &ldquo;
                            {props.quote}
                            &rdquo;
                        </p>
                        <img
                            css={portraitStyles}
                            src={props.portrait}
                            alt={props.name}
                        />
                        <img
                            css={brandStyles}
                            src={props.brand}
                            alt={props.title}
                        />
                        <p className="body-sm name" css={nameStyles}>
                            {props.name}
                        </p>
                        <p className="fine-print" css={titleStyles}>
                            {props.title}
                        </p>
                    </div>
                    {props.customerCTAUrl && (
                        <div css={learnMoreWrapperDesktop} className="desktop">
                            <Link
                                css={customerCTAStyle}
                                text={learnMoreText}
                                href={props.customerCTAUrl}
                                dataEventProps={{
                                    "data-product-key": "home",
                                    "data-event": "clicked",
                                    "data-event-component": "link",
                                    "data-event-container":
                                        "scrollPinnedSection",
                                    "data-label": learnMoreText,
                                    "data-label-english": learnMoreText
                                }}
                                type={"basic"}
                                doesArrowAnimate={true}
                            />
                        </div>
                    )}
                </div>
                <div className="img-region" css={imgRegionStyles}>
                    <div
                        className="bg-shape"
                        css={bgShapeStyles}
                        style={{
                            clipPath: `polygon(${props.bgShape})`,
                            backgroundColor: `#${props.bgColor}`
                        }}
                    />
                    <div className="img-accent" css={imgAccentStyles}>
                        {props.accentImg}
                    </div>
                    <img
                        css={imgScreenshotStyles}
                        src={props.posterImage}
                        className="img-screenshot"
                        alt=""
                    />
                </div>
                {props.productCTA?.url && (
                    <Adg4Button
                        css={ctaButtonStyles}
                        className="cta-button"
                        variant="primary"
                        color="blue"
                        href={props.productCTA?.url}
                        dataEventProps={{
                            "data-product-key": "home",
                            "data-event": "clicked",
                            "data-event-component": "linkButton",
                            "data-event-container": "ctaSection",
                            "data-label": props.productCTA?.text,
                            "data-label-english": props.productCTA?.text
                        }}
                    >
                        {props.productCTA?.text}
                    </Adg4Button>
                )}
                {props.customerCTAUrl && (
                    <div css={learnMoreWrapperMobile} className="mobile">
                        <Link
                            css={customerCTAStyle}
                            text={learnMoreText}
                            href={props.customerCTAUrl}
                            dataEventProps={{
                                "data-product-key": "home",
                                "data-event": "clicked",
                                "data-event-component": "link",
                                "data-event-container": "scrollPinnedSection",
                                "data-label": learnMoreText,
                                "data-label-english": learnMoreText
                            }}
                            type={"basic"}
                            doesArrowAnimate={true}
                        />
                    </div>
                )}
            </div>
        </UFOSegment>
    );
};

const teamTypeStyles = css({
    position: "relative",
    marginBlockEnd: "200px",
    [media.below.md]: {
        marginBlockEnd: "40px"
    }
});

const imgScreenshotStyles = css({
    position: "relative",
    display: "block",
    height: "auto",
    width: "100%",
    margin: "auto",
    animation: "fadein 0.25s 1",
    borderRadius: "5px",
    border: "3px solid #F0F1F2"
});

const teamCardStyles = css({
    [media.below.md]: {
        opacity: 1
    }
});

const imgRegionStyles = css({
    display: "none",
    position: "relative",
    aspectRatio: "7/4",
    width: "100%",
    padding: "15px",
    margin: "30px auto 40px",

    [media.below.md]: {
        display: "block"
    }
});

const bgShapeStyles = css({
    width: "100%",
    height: "100%",
    transition: "all 1s",
    position: "absolute",
    insetInlineStart: 0,
    insetBlockStart: 0
});

const imgAccentStyles = css({
    width: "20%",
    position: "absolute",
    insetInlineStart: "-3%",
    insetBlockEnd: "-10%",

    [media.below.md]: {
        insetBlockEnd: "-11%",
        insetInlineStart: "-6%"
    }
});

const ctaButtonStyles = css({
    display: "none",

    [media.below.md]: {
        display: "block",
        margin: "0 20px"
    }
});

const logoStyles = css({
    display: "flex",
    margin: "0 0 10px 0",
    alignItems: "center"
});

const h4Styles = css({
    margin: "0 0 0 10px"
});

const h3Styles = css({
    fontSize: "2rem",
    margin: 0
});
const descriptionStyles = css({
    marginInlineEnd: "30px",
    marginBlockStart: "12px"
});
const quoteStyles = css({
    backgroundColor: "#f8f8f8",
    padding: "30px 20px 30px 100px",
    position: "relative",
    marginBlockStart: "32px"
});

const portraitStyles = css({
    position: "absolute",
    width: "68px",
    insetInlineStart: "20px",
    insetBlockStart: "20px"
});
const brandStyles = css({
    position: "absolute",
    width: "30px",
    insetInlineStart: "60px",
    insetBlockStart: "60px"
});
const quotePStyles = css({
    position: "relative",
    margin: "0 0 1rem"
});
const nameStyles = css({
    position: "relative",
    margin: 0,
    fontWeight: 700
});
const titleStyles = css({
    position: "relative",
    margin: 0,
    textTransform: "uppercase"
});
const customerCTAStyle = css({
    marginBlockStart: "32px",

    [media.below.md]: {
        marginBlockStart: "12px"
    }
});

const learnMoreWrapperMobile = css({
    display: "none",

    [media.below.md]: {
        display: "flex",
        justifyContent: "center"
    }
});

const learnMoreWrapperDesktop = css({
    [media.below.md]: {
        display: "none"
    }
});
